import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const apiURL = process.env.REACT_APP_API_URL;

// Create axios instance
const axiosInstance = axios.create({
    baseURL: `https://${apiURL}`,
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Function to refresh the token
async function refreshToken(): Promise<string> {
    try {
        // Get stored email from localStorage (you'll need to store this during login)
        const userEmail = localStorage.getItem('userEmail');
        const oldToken = localStorage.getItem('authToken');
        
        if (!userEmail || !oldToken) {
            throw new Error('No user information found');
        }

        const response = await axios.post(`https://${apiURL}/Token/Refresh`, {
            email: userEmail,
            token: oldToken
        });

        const newToken = response.data.token;
        localStorage.setItem('authToken', newToken);
        
        return newToken;
    } catch (error) {
        // If refresh fails, clear token and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('userEmail');
        window.location.href = '/';
        throw error;
    }
}

// Add subscribers to queue
function subscribeTokenRefresh(cb: (token: string) => void) {
    refreshSubscribers.push(cb);
}

// Notify all subscribers when token is refreshed
function onTokenRefreshed(token: string) {
    refreshSubscribers.forEach(cb => cb(token));
    refreshSubscribers = [];
}

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Content-Type'] = 'application/json';
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // Check if error is 401 and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If already refreshing, wait for the new token
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token: string) => {
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                        }
                        resolve(axiosInstance(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newToken = await refreshToken();
                isRefreshing = false;
                onTokenRefreshed(newToken);
                
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

async function GetJsonData(controllerAction: string) {
    const token = localStorage.getItem('authToken');
    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axiosInstance.get(`/${controllerAction}`);
    const { data } = response;
    return data;
}

export default GetJsonData